<script setup>
import { ref, computed } from 'vue';
import LucideIcon from 'dashboard/components/LucideIcon.vue';

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  placeholder: {
    type: String,
    default: '',
  },
  hasError: {
    type: Boolean,
    default: false,
  },
  errorMessage: {
    type: String,
    default: '',
  },
  spacing: {
    type: String,
    default: 'base',
    validator: value => ['base', 'compact'].includes(value),
  },
  required: {
    type: Boolean,
    default: false,
  },
  autocomplete: {
    type: String,
    default: 'current-password',
  },
  dataTestid: {
    type: String,
    default: '',
  },
  tabindex: {
    type: [String, Number],
    default: undefined,
  },
});

defineOptions({
  inheritAttrs: false,
});

const model = defineModel({
  type: String,
  required: true,
});

const showPassword = ref(false);

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value;
};

const inputType = computed(() => showPassword.value ? 'text' : 'password');
const iconName = computed(() => showPassword.value ? 'eye-closed' : 'eye');
</script>

<template>
  <div class="space-y-2">
    <label 
      v-if="label"
      :for="name"
      class="block text-sm text-gray-600 font-semibold"
    >
      {{ label }}
      <slot name="rightOfLabel" />
    </label>
    <div class="relative">
      <input
        v-bind="$attrs"
        v-model="model"
        :type="inputType"
        :name="name"
        :id="name"
        :placeholder="placeholder"
        :required="required"
        :autocomplete="autocomplete"
        :data-testid="dataTestid"
        :tabindex="tabindex"
        class="block w-full rounded-md appearance-none text-n-gray-5 bg-n-gray-25 dark:bg-n-vars-black-br/30 border border-n-gray-1 dark:border-n-gray-8 mb-0 placeholder:text-slate-400 sm:text-sm sm:leading-6 pr-10"
        :class="{
          'border-red-400 dark:border-red-400': hasError,
          'focus:border-n-gray-3 dark:focus:border-n-gray-6': !hasError,
          'px-3 py-3': spacing === 'base',
          'px-3 py-2 mb-0': spacing === 'compact',
        }"
      />
      <button
        type="button"
        class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
        @click="togglePasswordVisibility"
        :aria-label="showPassword ? 'Hide password' : 'Show password'"
      >
        <LucideIcon 
          :name="iconName" 
          :size="20" 
          class="w-5 h-5"
        />
      </button>
    </div>
    <span
      v-if="errorMessage && hasError"
      class="text-xs text-red-400 leading-2"
    >
      {{ errorMessage }}
    </span>
  </div>
</template>

<style scoped>
/* Hide browser's native password toggle */
:deep(input[type="password"]::-ms-reveal) {
  display: none;
}

:deep(input[type="password"]::-webkit-credentials-auto-fill-button) {
  display: none;
}
</style>
